import React, { useState,useEffect } from "react";
import { toast } from 'react-toastify';
import {format } from 'date-fns';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { getDefaultReviewList,getSingleReviewTemplateDetail,searchEtsyReview } from "../../../../utils/reviewApi";
import { getSingleWidgetDetail } from "../../../../staticWidgets/utils/CommonWidgetApi";
import RatingStars from "../../../../components/RatingStars";
import ReviewPopup from "./ReviewPopup";
import BadgeWidget from "./BadgeWidget";
import FloatingWidget from "./FloatingWidget";

const CommonWidget = ({selectedLayout,isTemplateId,widgetId,reviewTabData}) => {
    const [apiData, setApiData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedReview, setSelectedReview] = useState(null);
    const [visibleGridCount, setVisibleGridCount] = useState(8);
    const [visibleListLayoutCount, setVisibleListLayoutCount] = useState(5);
    const [visibleMasonryLayoutCount, setVisibleMasonryLayoutCount] = useState(8);

    // Default values for the reviewTabData
    const {
        is_website_frontend,
        layout_is_full_width,
        business_name,
        layout_custom_width,
        style_background_color,
        header_is_widget_title,
        header_title,
        header_caption,
        setting_rating_format,
        style_widget_title_color,
        style_widget_title_fonts_title_size,
        style_widget_title_caption_color,
        style_widget_title_fonts_caption_size,
        style_header_background_color,
        header_is_show_header,
        header_write_reviews_button,
        header_source_icon,
        header_rating,
        header_number_of_reviews,
        style_header_text_color,
        style_header_write_review_background_color,
        style_header_write_review_text_color,
        header_write_review_button_type,
        style_rating_stars_color,
        layout_rows_on_mobile,
        layout_rows,
        style_load_more_button_color,
        style_load_more_button_text_color
    } = reviewTabData || {};

    // Map layout name to a specific class
    const layoutClassMap = {
        carousel_layout: 'upgadeify-carousel-style',
        list_layout: 'upgadeify-list-style',
        grid_layout: 'upgadeify-grid-style',
        masonry_layout: 'upgadeify-masonry-style',
        slider_layout: 'upgadeify-slider-style',
    };
    const layoutClass = layoutClassMap[selectedLayout] || '';

    //Helper to get slidesToShow from width
    const getSlidesToShow = (width) => {
        const w = Number(width);
        if (w >= 1024) return 3;
        if (w >= 768) return 2;
        return 1;
    };
    //State use for handle responsive logic
    const [slidesToShow, setSlidesToShow] = useState(getSlidesToShow(layout_custom_width));

    //Update slidesToShow when window resizes or width changes
    useEffect(() => {
        const handleResize = () => {
        setSlidesToShow(getSlidesToShow(layout_custom_width));
        };
        handleResize();
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, [layout_custom_width]);

    //Carousel Slider Settings
    const carouselSliderSettings = {
        dots: false,
        infinite: true,
        speed: 500,
        slidesToShow: slidesToShow,
        slidesToScroll: 1,
        arrows: true,
        autoplay: false,
        responsive: [
        {
            breakpoint: 768,
            settings: {
                slidesToShow: 1,
                slidesToScroll: 1,
                arrows: true,
                dots: false,
            },
        },
        ],
    };

    //Slider Settings
    const sliderSettings = {
        dots: true,
        infinite: true,
        speed: 500,
        slidesToShow: slidesToShow,
        slidesToScroll: 3,
        arrows: false,
        autoplay: false,
        responsive: [
            {
                breakpoint: 768,
                settings: {
                    slidesToShow: 1,
                    slidesToScroll: 1,
                    arrows: true,
                    dots: false,
                },
            },
        ],
    };

    // Fetching Review data
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                let result;
                if (isTemplateId) {
                    result = await getSingleReviewTemplateDetail(isTemplateId);
                } else if (widgetId) {
                    result = await getSingleWidgetDetail(widgetId);
                } else if (business_name) {
                    const payload = { business_name: business_name };
                    result = await searchEtsyReview(payload);
                } else {
                    let app_type = 'Etsy_Review';
                    result = await getDefaultReviewList(app_type);
                }
                setApiData(result); 
            } catch (error) {
                setApiData(null); 
                toast.warning(error.message);
            } finally {
                setLoading(false); 
            }
        };

        fetchData();
    }, [isTemplateId, widgetId, business_name]);

    //Handle Layout Rows and Columns
    const layoutColumns = 3;
    // Simple: detect if the screen is mobile once at render time
    const isMobile = window.innerWidth <= 768;
    const layoutRows = isMobile
        ? layout_rows_on_mobile
        : layout_rows;

    useEffect(() => {
        const newGridCount = layoutRows * layoutColumns;
        const newMasonryCount = layoutRows * layoutColumns;

        setVisibleGridCount(newGridCount);
        setVisibleListLayoutCount(layoutRows);
        setVisibleMasonryLayoutCount(newMasonryCount);
    }, [layoutRows, layoutColumns]);

    // Open popup when clicking "Read More"
    const handleReadMoreClick = (review) => {
        setSelectedReview(review);
    };

    // Close the popup
    const handleClosePopup = () => {
        setSelectedReview(null);
    };

    // Load More List Layout function
    const handleGridLoadMore = () => {
        setVisibleGridCount((prevCount) => prevCount + 8);
    };
    
    // Load List Layout More function
    const handleListLayoutLoadMore = () => {
        setVisibleListLayoutCount((prevCount) => prevCount + 5);
    };

    // Load Masonry Layout More function
    const handleMasonryLayoutLoadMore = () => {
        setVisibleMasonryLayoutCount((prevCount) => prevCount + 8);
    };

    //Handle dynamic styles based on layout
    const dynamicStyle =
        selectedLayout !== 'badge_layout' && selectedLayout !== 'floating_badge_layout'
            ? Number(layout_is_full_width) === 1
            ? {
                backgroundColor: style_background_color,
                margin: '0 auto',
            }
            : {
                backgroundColor: style_background_color,
                maxWidth: `${layout_custom_width}px`,
                margin: '0 auto',
                }
            : {};

    // Get grid layout style based on custom width
    const getGridLayoutStyle = () => {
        if (selectedLayout !== 'grid_layout') return {};

        if (layout_custom_width > 1280 && layout_custom_width < 1921) {
            return {
                gridTemplateColumns: 'repeat(auto-fill, minmax(24%, 1fr))',
            
            };
        }

        if (layout_custom_width > 991 && layout_custom_width < 1280) {
            return {
                gridTemplateColumns: 'repeat(auto-fill, minmax(32%, 1fr))',
        
            };
        }

        if (layout_custom_width > 540 && layout_custom_width < 991) {
            return {
                gridTemplateColumns: 'repeat(auto-fill, minmax(48%, 1fr))',
            
            };
        }

        if (layout_custom_width > 0 && layout_custom_width < 540) {
            return {
                gridTemplateColumns: 'repeat(auto-fill, minmax(100%, 1fr))',
            
            };
        }

        // Default fallback
        return {
            gridTemplateColumns: 'repeat(auto-fill, minmax(32%, 1fr))',
        };
    };

    return (
        <div className={`upgadeify-review-template-widget upgadeify ${layoutClass} ${selectedReview ? 'carousel-popup-open' : ''} ${is_website_frontend}`} style={dynamicStyle}>
             {/* Loader and No Api Data Handling */}
            {loading ? (
                <div className="loader-container">
                    <div className="spinner"></div>
                    <p>Loading please wait...</p>
                </div>
            ) : apiData?.status === true ? (
            <>
                {/* Show Header Section Accordgin to condition*/}
                {selectedLayout !== 'badge_layout' && selectedLayout !== 'floating_badge_layout' && (
                    <>
                    {Number(header_is_widget_title) === 1 && (
                        <>
                            <div className="upgadeify-etsy-main-title" style={{ color: style_widget_title_color,fontSize: style_widget_title_fonts_title_size }}>{header_title || ""}</div>
                            <div className="upgadeify-etsy-main-caption" style={{ color: style_widget_title_caption_color,fontSize: style_widget_title_fonts_caption_size }}>{header_caption || ""}</div>
                        </>
                    )}
                    {Number(header_is_show_header) === 1 &&
                        (Number(header_source_icon) === 1 ||
                            Number(header_rating) === 1 ||
                            Number(header_number_of_reviews) === 1 ||
                            Number(header_write_reviews_button) === 1) && (
                            <div className="upgadeify-etsy-reviews-header" style={{ backgroundColor: style_header_background_color,}}>
                        
                                <div className='upgadeify-etsy-reviews-header-left'>
                                    {Number(header_source_icon) === 1 && (
                                    <div className='upgadeify-etsy-reviews-header-title'>
                                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 51 36" className="injected-svg"><g clip-path="url(#a-196)"><path fill="#F45800" d="M5.43 7.826v6.813s2.401 0 3.686-.098c1.01-.177 1.197-.274 1.383-1.284l.371-1.48h1.1l-.187 3.234.098 3.313h-1.108l-.274-1.294c-.275-.92-.647-1.108-1.383-1.196-.92-.098-3.685-.098-3.685-.098v5.715c0 1.099.558 1.568 1.842 1.568h3.872c1.196 0 2.392-.097 3.137-1.843l1-2.214h.93c-.097.46-.557 4.518-.646 5.43 0 0-3.5-.088-4.97-.088H3.96L0 24.392v-1.001l1.285-.283c.93-.186 1.204-.46 1.204-1.197 0 0 .098-2.489.098-6.636 0-4.137-.098-6.627-.098-6.627 0-.825-.274-1.01-1.204-1.196L0 7.179V6.17l3.871.08h7.371c1.471 0 3.951-.266 3.951-.266s-.088 1.56-.186 5.245h-1.01l-.372-1.294c-.363-1.657-.913-2.481-1.931-2.481H5.9c-.47 0-.47.09-.47.373Zm14.92.727h1.107v3.871l3.784-.186-.186 1.754-3.686-.283v6.822c0 1.931.646 2.675 1.754 2.675 1.002 0 1.746-.558 2.02-1.018l.549.646c-.549 1.285-2.108 1.931-3.588 1.931-1.834 0-3.323-1.098-3.323-3.216v-7.744h-2.197v-.92c1.843-.187 3.217-1.294 3.766-4.332Zm7.929 11.88.734 1.754c.275.735.921 1.48 2.392 1.48 1.568 0 2.215-.833 2.215-1.843 0-3.137-6.175-2.215-6.175-6.45 0-2.392 1.931-3.509 4.42-3.509 1.108 0 2.765.186 3.597.558-.186.92-.283 2.117-.283 3.04l-.913.088-.646-1.843c-.186-.451-.93-.825-1.843-.825-1.108 0-2.215.46-2.215 1.657 0 2.853 6.36 2.215 6.36 6.45 0 2.401-2.116 3.686-4.703 3.686-1.931 0-3.872-.744-3.872-.744.186-1.099.098-2.294 0-3.5h.932Zm7.185 8.841c.274-1.01.46-2.294.646-3.5l1.01-.088.372 1.931c.089.46.363.833 1.1.833 1.196 0 2.764-.735 4.243-3.314-.656-1.568-2.587-6.627-4.341-10.498-.46-1.019-.55-1.108-1.188-1.294l-.47-.177v-.93l2.773.098 3.402-.186v.921l-.832.187c-.646.088-.913.45-.913.824 0 .097 0 .186.089.372.177.558 1.657 4.696 2.764 7.46.913-1.932 2.668-6.264 2.942-7 .097-.372.186-.46.186-.735 0-.47-.275-.744-.913-.92l-.655-.188v-.92l2.586.088 2.392-.088v.92l-.46.363c-.921.372-1.019.46-1.383 1.205l-4.048 9.479c-2.4 5.44-4.89 5.9-6.636 5.9-1.107.001-1.94-.282-2.666-.743Z"></path></g><defs><clipPath id="a-196"><path fill="#fff" d="M0 0h50.657v36H0z"></path></clipPath></defs></svg>
                                        <div className='upgadeify-etsy-reviews-header-title-text' style={{color: style_header_text_color,}}>Reviews</div>
                                    </div>
                                    )}
                                    <div className='upgadeify-etsy-reviews-header-rating'>
                                        {Number(header_rating) === 1 && (
                                        <>
                                            <div className="upgadeify-etsy-reviews-header-rating-text" style={{color: style_header_text_color,}}>{setting_rating_format === '4.9' ? Number(apiData.data.average_rating).toFixed(1) : Number(apiData.data.average_rating).toFixed(2) }</div>
                                            <div className="upgadeify-etsy-reviews-item-rating">
                                                <RatingStars average_rating={apiData.data.average_rating} newColor={style_rating_stars_color} />
                                            </div>
                                        </>
                                        )}
                                        {Number(header_number_of_reviews) === 1 && (
                                            <span className="upgadeify-etsy-reviews-header-number-of-reviews-text">({apiData.data.total_reviews})</span>
                                        )}
                                    </div>
                                </div>
                                {Number(header_write_reviews_button) === 1 && (
                                    <div className='upgadeify-etsy-reviews-header-right'>
                                        <a
                                            href={apiData?.data?.google_url?.startsWith('http') ? apiData.data.google_url : `https://${apiData.data.google_url}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className='upgadeify-etsy-reviews-header-button'
                                            style={{color: style_header_write_review_text_color,backgroundColor: style_header_write_review_background_color,fontSize: header_write_review_button_type}}>Review us on Etsy
                                        </a>
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                )}

                {/*If Carousel Layout Type*/}
                {selectedLayout === 'carousel_layout' && (
                    <Slider {...carouselSliderSettings}>
                        {apiData.data.review_list.map((review) => (
                            <ReviewLoopItem key={review.id} review={review} handleReadMoreClick={handleReadMoreClick} reviewTabData={reviewTabData}/>
                        ))}
                    </Slider>
                )}

                {/*If List Layout Type*/}
                {selectedLayout === 'list_layout' && (
                    <>
                        <div className='upgadeify-list_layout-layout'>
                            {apiData.data.review_list.slice(0, visibleListLayoutCount).map((review) => (
                                <ReviewLoopItem key={review.id} review={review} handleReadMoreClick={handleReadMoreClick} reviewTabData={reviewTabData}/>
                            ))}
                        </div>
                        {/* Load More Button  */}
                        {visibleListLayoutCount < apiData.data.review_list.length && (
                            <div className="load-more-button">
                                <button onClick={handleListLayoutLoadMore} style={{backgroundColor: style_load_more_button_color,color: style_load_more_button_text_color}}>Load More</button>
                            </div>
                        )}
                    </>
                )}

                {/*If Grid Layout Type*/}
                {selectedLayout === 'grid_layout' && (
                    <>
                        <div className='upgadeify-grid-layout' style={getGridLayoutStyle()}>
                            {apiData.data.review_list.slice(0, visibleGridCount).map((review) => (
                                <ReviewLoopItem key={review.id} review={review} handleReadMoreClick={handleReadMoreClick} reviewTabData={reviewTabData}/>
                            ))}
                        </div>
                        {visibleGridCount < apiData.data.review_list.length && (
                            <div className="load-more-button">
                                <button onClick={handleGridLoadMore} style={{backgroundColor: style_load_more_button_color,color: style_load_more_button_text_color}}>Load More</button>
                            </div>
                        )}
                    </>
                )}

                {/*If Masonry Layout Type*/}
                {selectedLayout === 'masonry_layout' && (
                    <>
                        <div className='upgadeify-masonry_layout-layout'>
                            {apiData.data.review_list.slice(0, visibleMasonryLayoutCount).map((review) => (
                                <ReviewLoopItem key={review.id} review={review} handleReadMoreClick={handleReadMoreClick} reviewTabData={reviewTabData}/>
                            ))}
                            
                        </div>
                            {/* Load More Button  */}
                            {visibleMasonryLayoutCount < apiData.data.review_list.length && (
                                <div className="load-more-button">
                                    <button onClick={handleMasonryLayoutLoadMore} style={{backgroundColor: style_load_more_button_color,color: style_load_more_button_text_color}}>Load More</button>
                                </div>
                            )}
                    </>
                )}

                {/*If Slider Layout Type*/}
                {selectedLayout === 'slider_layout' && (
                    <Slider {...sliderSettings}>
                        {apiData.data.review_list.map((review) => (
                            <ReviewLoopItem key={review.id} review={review} handleReadMoreClick={handleReadMoreClick} reviewTabData={reviewTabData} />
                        ))}
                    </Slider>
                )}

                {/*If Badge Layout Type*/}
                {selectedLayout === 'badge_layout' && (
                    <BadgeWidget selectedLayout={selectedLayout} isTemplateId={isTemplateId} widgetId={widgetId} reviewTabData={reviewTabData} />
                )}

                {/*If Floating Badge Layout Type*/}
                {selectedLayout === 'floating_badge_layout' && (
                    <FloatingWidget selectedLayout={selectedLayout} isTemplateId={isTemplateId} widgetId={widgetId} reviewTabData={reviewTabData} />
                )}

                {/*Common Popup */}
                {selectedReview && <ReviewPopup review={selectedReview} onClose={handleClosePopup} />}
            </>
            ) : (
                <div className="no-inner-result-found">
                    <p>No Etsy reviews found please try.</p>
                </div>
            )}
        </div >
    );
};

const ReviewLoopItem = ({ review, handleReadMoreClick, reviewTabData }) => (
    <>
        <div className="upgadeify-etsy-reviews">
            <div className='upgadeify-etsy-reviews-item' style={{ padding: `${reviewTabData.layout_item_spacing}px`, backgroundColor: reviewTabData.style_review_background_color,}}>
                <div className='upgadeify-etsy-reviews-head'>
                    {(Number(reviewTabData.review_is_show_reviewer_picture) === 1 || Number(reviewTabData.review_is_show_verified_badge) === 1) && (
                        <div className="upgadeify-etsy-reviews-item-image">
                            {review.image ? (
                                <img src={review.image} alt="Review" />
                            ) : (
                                <div className="fallback-avatar">
                                    {review.name.charAt(0)}
                                </div>
                            )}
                           
                        </div>
                    )}
                    <div className='upgadeify-etsy-reviews-item-name'>
                        {Number(reviewTabData.review_is_show_reviewer_name) === 1 && (
                        Number(reviewTabData.setting_is_enable_external_links) === 1 && review.author_url ? ( 
                                <a
                                    href={review.author_url} 
                                    target={Number(reviewTabData.setting_is_open_links_in_new_tab) === 1 ? "_blank" : "_self"}
                                    rel={Number(reviewTabData.setting_is_open_links_in_new_tab) === 1 ? "noopener noreferrer" : undefined}
                                    style={{ color: reviewTabData.style_review_reviewer_name_color }}
                                >
                                    <span className="upgadeify-etsy-reviews-item-name-text" style={{ color: reviewTabData.style_review_reviewer_name_color }}>{review.name}</span>
                                </a>
                            ) : (
                                <span className="upgadeify-etsy-reviews-item-name-text" style={{ color: reviewTabData.style_review_reviewer_name_color }}>
                                    {review.name}
                                </span>
                            )
                        )}

                        {Number(reviewTabData.review_is_show_verified_badge) === 1 && (
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 14 14" className="review-verified-badge-icon" aria-hidden="true">
                                <path fill={reviewTabData.style_review_verified_badge_color} d="M6.757.236a.35.35 0 0 1 .486 0l1.106 1.07a.35.35 0 0 0 .329.089l1.493-.375a.35.35 0 0 1 .422.244l.422 1.48a.35.35 0 0 0 .24.24l1.481.423a.35.35 0 0 1 .244.422l-.375 1.493a.35.35 0 0 0 .088.329l1.071 1.106a.35.35 0 0 1 0 .486l-1.07 1.106a.35.35 0 0 0-.089.329l.375 1.493a.35.35 0 0 1-.244.422l-1.48.422a.35.35 0 0 0-.24.24l-.423 1.481a.35.35 0 0 1-.422.244l-1.493-.375a.35.35 0 0 0-.329.088l-1.106 1.071a.35.35 0 0 1-.486 0l-1.106-1.07a.35.35 0 0 0-.329-.089l-1.493.375a.35.35 0 0 1-.422-.244l-.422-1.48a.35.35 0 0 0-.24-.24l-1.481-.423a.35.35 0 0 1-.244-.422l.375-1.493a.35.35 0 0 0-.088-.329L.236 7.243a.35.35 0 0 1 0-.486l1.07-1.106a.35.35 0 0 0 .089-.329L1.02 3.829a.35.35 0 0 1 .244-.422l1.48-.422a.35.35 0 0 0 .24-.24l.423-1.481a.35.35 0 0 1 .422-.244l1.493.375a.35.35 0 0 0 .329-.088L6.757.236Z"></path><path fill="#fff" fill-rule="evenodd" d="M9.065 4.85a.644.644 0 0 1 .899 0 .615.615 0 0 1 .053.823l-.053.059L6.48 9.15a.645.645 0 0 1-.84.052l-.06-.052-1.66-1.527a.616.616 0 0 1 0-.882.645.645 0 0 1 .84-.052l.06.052 1.21 1.086 3.034-2.978Z" clip-rule="evenodd"></path>
                            </svg>
                        )}

                        <span className="upgadeify-etsy-reviews-item-date">
                            {Number(reviewTabData.review_is_show_date) === 1 && (
                                <span style={{color: reviewTabData.style_review_date_color,}}>{review.review_date ? format(new Date(review.review_date), 'yyyy-MM-dd') : ''}</span>
                            )}
                        </span>
                    </div>
                </div>
                <div className="upgadeify-etsy-reviews-item-content">
                   {Number(reviewTabData.review_is_show_rating) === 1 && (
                        <span className="upgadeify-etsy-reviews-item-rating" style={{ fontSize: reviewTabData.style_review_rating_size }}>
                            <RatingStars average_rating={review.rating} newColor={reviewTabData.style_rating_stars_color}/>
                        </span>
                    )}
                    <div className="upgadeify-etsy-reviews-item-text" style={{color: `${reviewTabData.style_review_text_color}`,fontSize: reviewTabData.style_review_text_font_size}}>
                        {review.description.length > 50
                        ? `${review.description.slice(0, 50)}...`
                        : review.description}
                    </div>
                    {review.description.length > 50 && (
                        <div className='upgadeify-etsy-reviews-readmore' onClick={() => handleReadMoreClick(review)} style={{color: reviewTabData.style_read_more_color,fontSize: reviewTabData.style_review_text_font_size}}>
                                Read more
                        </div>
                    )}

                    {Number(reviewTabData.review_is_show_review_source) === 1 && (  
                        <div className='upgadeify-etsy-reviews-item-image-badge'>
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 25" className="injected-svg"><circle cx="12" cy="12.5" r="10" fill="#F45800"></circle><path fill="#fff" fill-rule="evenodd" d="M10.68 8.501v3.7s1.184 0 1.818-.052c.499-.096.59-.15.682-.698l.184-.804h.542l-.092 1.757.049 1.8h-.547l-.136-.703c-.135-.5-.319-.602-.682-.65-.454-.053-1.818-.053-1.818-.053v3.104c0 .597.275.852.909.852h1.91c.59 0 1.18-.053 1.548-1.001l.494-1.203H16c-.048.25-.275 2.454-.319 2.95 0 0-1.727-.048-2.452-.048H9.954L8 17.5v-.544l.634-.154c.459-.1.594-.25.594-.65 0 0 .048-1.352.048-3.605 0-2.247-.048-3.6-.048-3.6 0-.447-.135-.548-.594-.65L8 8.15V7.6l1.91.043h3.637c.726 0 1.95-.144 1.95-.144s-.044.847-.092 2.849h-.498l-.184-.703c-.179-.9-.45-1.348-.953-1.348h-2.859c-.231 0-.231.049-.231.203Z" clip-rule="evenodd"></path></svg>
                            Posted On   
                            <> <a href={`https://www.etsy.com`} target="blank">Etsy</a></> 
                        </div> 
                    )} 
                </div>
            </div>
        </div>
    </>
);

export default CommonWidget;
